/**
 * Field-capture byte storage helpers — iOS-safe. Covers photos (getPhotoBlob)
 * and voice notes (getVoiceBlob); both hit the identical purge bug below.
 *
 * iOS/WebKit corrupts, truncates, or empties `Blob`s persisted in IndexedDB:
 * the record survives but its bytes read back as 0 (or null) after navigation /
 * storage eviction / a long session. It's intermittent and device-dependent,
 * and Android/Chromium is unaffected. See Dexie #1305 / #618 / #1227 and
 * WebKit #198278. A purged blob renders as a dead thumbnail ("Photo
 * unavailable") and is rejected by the backend as "Empty file" on sync.
 *
 * The fix: never store a `Blob` in IndexedDB. Store the raw bytes as an
 * `ArrayBuffer` (stored inline, not as a purgeable file reference) and
 * reconstruct the Blob on read for display/upload.
 */

/** A photo's binary payload as persisted — new `bytes` form or the legacy Blob. */
export interface StoredPhotoBytes {
  /** Preferred, iOS-safe storage: raw image bytes. */
  bytes?: ArrayBuffer | null
  /** Mime type to reconstruct the Blob with. */
  mimeType?: string | null
  /** Legacy field: a Blob written before the ArrayBuffer migration. Read-only. */
  blob?: Blob | null
}

/** True only when the blob exists and actually has bytes (rejects 0-byte captures). */
export function hasImageBytes(blob: Blob | null | undefined): boolean {
  return !!blob && blob.size > 0
}

/**
 * Convert a Blob to an ArrayBuffer. MUST be called BEFORE opening the IndexedDB
 * write — an IDB transaction auto-commits across an `await`, so converting
 * inside one throws InvalidStateError (Dexie #1227).
 */
export async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    return await blob.arrayBuffer()
  }
  // Fallback for environments without Blob.prototype.arrayBuffer (Safari < 14).
  return await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed reading blob'))
    reader.readAsArrayBuffer(blob)
  })
}

/**
 * Reconstruct a usable Blob from a stored photo. Prefers the iOS-safe `bytes`,
 * falls back to a legacy `blob` for records written before the migration, and
 * returns `null` when neither holds any bytes (a corrupted / empty record).
 */
export function getPhotoBlob(photo: StoredPhotoBytes): Blob | null {
  if (photo.bytes && photo.bytes.byteLength > 0) {
    return new Blob([photo.bytes], { type: photo.mimeType || 'image/jpeg' })
  }
  if (hasImageBytes(photo.blob)) {
    return photo.blob as Blob
  }
  return null
}

/** A voice note's binary payload as persisted — new `bytes` form or the legacy Blob. */
export interface StoredVoiceBytes {
  /** Preferred, iOS-safe storage: raw audio bytes. */
  bytes?: ArrayBuffer | null
  /** Mime type to reconstruct the audio Blob with. */
  mimeType?: string | null
  /** Legacy field: a Blob written before the ArrayBuffer migration. Read-only. */
  blob?: Blob | null
}

/**
 * Reconstruct a usable audio Blob from a stored voice note. The audio twin of
 * getPhotoBlob — same iOS IndexedDB Blob-purge rationale (see the module header):
 * prefer the iOS-safe inline `bytes`, fall back to a legacy `blob` for notes
 * written before the migration, and return `null` when neither holds any bytes
 * (a purged / empty recording, which the backend would reject as a 400 on sync).
 */
export function getVoiceBlob(note: StoredVoiceBytes): Blob | null {
  if (note.bytes && note.bytes.byteLength > 0) {
    return new Blob([note.bytes], { type: note.mimeType || 'audio/webm' })
  }
  if (note.blob && note.blob.size > 0) {
    return note.blob
  }
  return null
}

/** One thumbnail-strip tile: a photo id with its object URL, or null when gone. */
export interface PhotoStripEntry {
  id: string
  /** Thumbnail object URL, or null when the image bytes are gone (empty/corrupt). */
  url: string | null
}

/**
 * Build thumbnail-strip entries for EVERY photo, in order. A photo whose bytes
 * were purged (getPhotoBlob -> null, so no object URL) still gets an entry with
 * `url: null`; the strip renders it as a deletable "empty — re-take" placeholder
 * so the inspector can remove and re-capture it. Filtering these out (as keying
 * the strip off the URL'd lightbox list did) hid the tile AND its delete button,
 * stranding the dead record in a permanently-unsynced state.
 */
export function buildPhotoStripEntries(
  photoIds: string[],
  urlFor: (id: string) => string | null | undefined,
): PhotoStripEntry[] {
  return photoIds.map((id) => ({ id, url: urlFor(id) ?? null }))
}

/** Minimal record shape needed to judge husk recoverability. */
export interface HuskCandidate extends StoredPhotoBytes {
  id: string
  synced: boolean
}

/**
 * Ids of local photo records that are safe to delete because they are
 * recoverable husks: their image bytes are gone (iOS purge) AND the upload
 * is confirmed on the server (synced flag set locally + id present in the
 * server's photo list). Deleting them lets the server copy's thumbnail
 * render instead of a dead tile — the field page only shows server photos
 * when no local records exist. Unsynced or unconfirmed empties are NOT
 * returned: with no server copy to recover from, they must stay visible as
 * deletable "re-take" tiles.
 */
export function findRecoverableHuskIds(
  localPhotos: HuskCandidate[],
  serverPhotoIds: Set<string>,
): string[] {
  return localPhotos
    .filter((p) => p.synced && serverPhotoIds.has(p.id) && getPhotoBlob(p) === null)
    .map((p) => p.id)
}
